// Client-side wiring for the logged-out profile signup autotest's JS-driven prompts
// (Social::Profile::Autotest::JsPromptsComponent renders the hidden markup; the socket's
// exit_intent_signup / sticky_signup_bar slots decide whether it exists at all — for
// control and non-E arms this module finds nothing and does nothing).
//
// Behavior contract (docs/superpowers/autotest/logged_out_profile/variants.md, E1/E2):
// - Exit intent (E1): desktop only (mouse-leave toward the top of the viewport), at most
//   once per session, dismissible via backdrop/X.
// - Sticky bar (E2): shown after the visitor scrolls past the first work card,
//   dismissible once per session, never re-shown after dismissal.
//
// Click tracking rides the generic [data-click-track] binder in profile.wjs.jsx — the
// CTAs are in the DOM from the server render, so no extra tracking wiring here.

const HIDDEN_CLASS = 'u-displayNone';
const EXIT_SHOWN_KEY = 'profileAutotestExitIntentShown';
const STICKY_DISMISSED_KEY = 'profileAutotestStickyDismissed';

const sessionFlag = (key: string): boolean => {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return true; // no sessionStorage (privacy mode) → behave as already shown
  }
};

const setSessionFlag = (key: string): void => {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // ignore — worst case the prompt stays suppressed
  }
};

const setUpExitIntent = (): void => {
  const found = document.querySelector<HTMLElement>('.js-autotest-exit-intent');
  if (!found || sessionFlag(EXIT_SHOWN_KEY)) return;
  const modal = found; // narrowed non-null; the hoisted handlers below close over this

  // WCAG AA: the dialog is keyboard-operable — Escape closes it, focus moves into it on
  // open and is restored on close, and Tab is trapped inside while it's open.
  let lastFocused: HTMLElement | null = null;

  const focusable = (): HTMLElement[] =>
    Array.from(
      modal.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'),
    ).filter((el) => el.offsetParent !== null);

  function closeModal(): void {
    modal.classList.add(HIDDEN_CLASS);
    document.removeEventListener('keydown', onKeydown);
    lastFocused?.focus();
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      closeModal();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusable();
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const openModal = (): void => {
    setSessionFlag(EXIT_SHOWN_KEY);
    lastFocused = document.activeElement as HTMLElement | null;
    modal.classList.remove(HIDDEN_CLASS);
    document.addEventListener('keydown', onKeydown);
    focusable()[0]?.focus();
  };

  const onMouseOut = (event: MouseEvent): void => {
    // Fire only when the pointer leaves through the top of the viewport (toward the
    // URL bar / tabs) — the conventional exit-intent heuristic, desktop-only by nature.
    if (event.relatedTarget || event.clientY > 0) return;

    document.removeEventListener('mouseout', onMouseOut);
    openModal();
  };

  modal.querySelectorAll('.js-autotest-exit-intent-dismiss').forEach((el) => {
    el.addEventListener('click', () => closeModal());
  });

  document.addEventListener('mouseout', onMouseOut);
};

const setUpStickyBar = (): void => {
  const bar = document.querySelector<HTMLElement>('.js-autotest-sticky-bar');
  if (!bar || sessionFlag(STICKY_DISMISSED_KEY)) return;

  const firstCard = document.querySelector('.js-work-strip');

  const onScroll = (): void => {
    const pastFirstCard = firstCard
      ? firstCard.getBoundingClientRect().bottom < 0
      : window.scrollY > window.innerHeight;
    if (!pastFirstCard) return;

    window.removeEventListener('scroll', onScroll);
    bar.classList.remove(HIDDEN_CLASS);
  };

  bar
    .querySelector('.js-autotest-sticky-dismiss')
    ?.addEventListener('click', () => {
      setSessionFlag(STICKY_DISMISSED_KEY);
      bar.classList.add(HIDDEN_CLASS);
    });

  window.addEventListener('scroll', onScroll, { passive: true });
};

export const setUpAutotestSignupPrompts = (): void => {
  setUpExitIntent();
  setUpStickyBar();
};
